import React, { ReactNode } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { ColumnsType } from "antd/es/table";
import {
  BacktestingRunType,
  BacktestingSort,
  BotType,
  LanguageNameSpaces,
  SortingOrder,
} from "@shared/types";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import Table from "common/Table/Table";
import { SORT_ORDER_MAPPING } from "common/constants";
import { getTableOnChangeFunction } from "common/Table/utils";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import BacktestStatusTag from "features/bots/BotForm/Backtesting/common/BacktestStatusTag";
import BacktestingActions from "features/bots/BotForm/Backtesting/common/BacktestingActions";
import {
  NameCell,
  NetPnlCell,
  SkeletonCell,
  TextCell,
} from "features/bots/BotForm/Backtesting/NewLook/BacktestCells";
import {
  formatClock,
  formatDay,
  formatTimeframe,
  getTimeframeDays,
} from "features/bots/BotForm/Backtesting/NewLook/format";

interface NewLookBacktestingTableProps {
  botType: BotType;
  data?: BacktestingRunType[];
  openDeleteModal?: (backtesting: BacktestingRunType) => void;
  onRowClick: (backtesting: BacktestingRunType) => void;
  emptyState: ReactNode;
  loading?: boolean;
  sortBy?: BacktestingSort;
  setSortBy: (sortBy: BacktestingSort) => void;
  sortDirection: SortingOrder;
  setSortDirection: (sortDirection: SortingOrder) => void;
}

// Figure columns are right-aligned, their titles over the figures.
const END_COLUMN = { align: "right", className: "nl-end" } as const;
// While loading, placeholder rows in the table itself, so the skeletons sit
// under their column headers.
const SKELETON_ROWS = Array.from(
  { length: 5 },
  (_, index) => ({ id: -1 - index } as BacktestingRunType)
);
const isSkeleton = (run: BacktestingRunType) => run.id < 0;

/**
 * New look: the backtests as rows to compare — what was tested and where,
 * over which period, when it ran, its status and its net PnL — with the
 * whole row opening the results.
 */
const NewLookBacktestingTable = ({
  botType,
  data,
  openDeleteModal,
  onRowClick,
  emptyState,
  loading,
  sortBy,
  setSortBy,
  sortDirection,
  setSortDirection,
}: NewLookBacktestingTableProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const displayCurrency = useSelector(selectDisplayCurrency);
  const getSortOrder = (sort: BacktestingSort) =>
    sortBy === sort ? SORT_ORDER_MAPPING[sortDirection] : undefined;
  const daysLabel = t("backtesting.labels.days");

  const columns: ColumnsType<BacktestingRunType> = [
    {
      title: t("backtesting.labels.backtest"),
      dataIndex: "name",
      showSorterTooltip: false,
      sorter: true,
      sortOrder: getSortOrder(BacktestingSort.NAME),
      render: (_, run) =>
        isSkeleton(run) ? (
          <SkeletonCell icon={true} width={150} />
        ) : (
          <NameCell run={run} botType={botType} />
        ),
    },
    {
      title: t("backtesting.labels.timeframe"),
      dataIndex: "backtesting_time_range.end",
      showSorterTooltip: false,
      sorter: true,
      sortOrder: getSortOrder(BacktestingSort.TIME_FRAME),
      render: (_, run) => {
        if (isSkeleton(run)) return <SkeletonCell width={120} />;
        const days = getTimeframeDays(run.backtesting_time_range);
        return (
          <TextCell
            value={formatTimeframe(run.backtesting_time_range)}
            meta={days ? `${days} ${daysLabel}` : undefined}
          />
        );
      },
    },
    {
      title: t("backtesting.labels.created_at"),
      dataIndex: "created_at",
      showSorterTooltip: false,
      sorter: true,
      sortOrder: getSortOrder(BacktestingSort.CREATED),
      render: (createdAt, run) =>
        isSkeleton(run) ? (
          <SkeletonCell width={84} />
        ) : (
          <TextCell
            value={formatDay(createdAt)}
            meta={formatClock(createdAt)}
          />
        ),
    },
    {
      title: t("backtesting.labels.status"),
      dataIndex: "status",
      showSorterTooltip: false,
      sorter: true,
      sortOrder: getSortOrder(BacktestingSort.STATUS),
      render: (_, run) =>
        isSkeleton(run) ? (
          <SkeletonCell width={76} single={true} />
        ) : (
          <BacktestStatusTag backtestingRun={run} />
        ),
    },
    {
      title: t("backtesting.labels.net_pnl"),
      dataIndex: `net_pnl.${displayCurrency}`,
      showSorterTooltip: false,
      sorter: true,
      sortOrder: getSortOrder(BacktestingSort.NET_PNL),
      ...END_COLUMN,
      render: (_, run) =>
        isSkeleton(run) ? (
          <SkeletonCell width={72} end={true} />
        ) : (
          <NetPnlCell run={run} />
        ),
    },
    {
      title: "",
      key: "actions",
      width: 56,
      render: (_, run) =>
        isSkeleton(run) ? null : (
          <BacktestingActions
            backtestingRun={run}
            openDeleteModal={() => openDeleteModal?.(run)}
            botType={botType}
          />
        ),
    },
  ];

  const handleChange = getTableOnChangeFunction<BacktestingSort>(
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    {
      name: BacktestingSort.NAME,
      [`net_pnl.${displayCurrency}`]: BacktestingSort.NET_PNL,
      status: BacktestingSort.STATUS,
      created_at: BacktestingSort.CREATED,
      ["backtesting_time_range.end"]: BacktestingSort.TIME_FRAME,
    }
  );

  const showSkeleton = loading && !data?.length;

  return (
    <TableWrapper>
      <Table<BacktestingRunType>
        columns={columns}
        dataSource={showSkeleton ? SKELETON_ROWS : data}
        onChange={handleChange}
        rowClassName={showSkeleton ? undefined : "u-cursorPointer"}
        rowKey={(run) => run.id}
        locale={{ emptyText: emptyState }}
        onRow={(run) =>
          isSkeleton(run)
            ? { "aria-busy": true }
            : {
                onClick: () => onRowClick(run),
                // Rows open the results, so they are reachable by keyboard too.
                tabIndex: 0,
                onKeyDown: (event: React.KeyboardEvent) => {
                  if (
                    event.key === "Enter" &&
                    event.target === event.currentTarget
                  )
                    onRowClick(run);
                },
              }
        }
      />
    </TableWrapper>
  );
};

export default NewLookBacktestingTable;

/**
 * The trades lists' table treatment: micro headers on a hairline, ~56px
 * rows with hairline separators, the dashboard's row hover and a keyboard
 * focus ring; first and last columns on the panel's 20px edges.
 */
const TableWrapper = styled.div`
  .ant-table-wrapper .ant-table {
    background: transparent;
  }
  .ant-table-wrapper .ant-table-thead > tr > th,
  .ant-table-wrapper .ant-table-thead > tr > td {
    height: 36px;
    padding: 8px 12px;
    ${newLookType.micro};
    color: ${(p) => getNewLookTokens(p.theme).muted};
    background: transparent !important;
    border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
    white-space: nowrap;
    &::before {
      display: none;
    }
  }
  .ant-table-wrapper .ant-table-thead th.ant-table-column-has-sorters:hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover} !important;
  }
  .ant-table-wrapper .ant-table-tbody > tr > td,
  .ant-table-wrapper .ant-table-tbody > tr > td.ant-table-column-sort {
    height: 56px;
    padding: 8px 12px;
    background: transparent;
    border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
    transition: background-color 120ms;
  }
  .ant-table-wrapper .ant-table-tbody > tr.ant-table-row:hover > td,
  .ant-table-wrapper
    .ant-table-tbody
    .ant-table-row
    > .ant-table-cell-row-hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover} !important;
  }
  .ant-table-wrapper .ant-table-tbody > tr.ant-table-row {
    outline: none;
  }
  .ant-table-wrapper .ant-table-tbody > tr.ant-table-row:focus-visible > td {
    box-shadow: inset 0 2px 0 ${(p) => getNewLookTokens(p.theme).focus},
      inset 0 -2px 0 ${(p) => getNewLookTokens(p.theme).focus};
  }
  .ant-table-wrapper
    .ant-table-tbody
    > tr.ant-table-row:focus-visible
    > td:first-child {
    box-shadow: inset 2px 2px 0 ${(p) => getNewLookTokens(p.theme).focus},
      inset 0 -2px 0 ${(p) => getNewLookTokens(p.theme).focus};
  }
  .ant-table-wrapper
    .ant-table-tbody
    > tr.ant-table-row:focus-visible
    > td:last-child {
    box-shadow: inset -2px 2px 0 ${(p) => getNewLookTokens(p.theme).focus},
      inset 0 -2px 0 ${(p) => getNewLookTokens(p.theme).focus};
  }
  .ant-table-wrapper .ant-table-thead > tr > th:first-child,
  .ant-table-wrapper .ant-table-tbody > tr > td:first-child {
    padding-left: 20px !important;
  }
  .ant-table-wrapper .ant-table-thead > tr > th:last-child,
  .ant-table-wrapper .ant-table-tbody > tr > td:last-child {
    padding-right: 12px !important;
  }
  .ant-table-wrapper .ant-table-tbody > tr.ant-table-placeholder > td {
    height: auto;
    padding: 0 !important;
    border-bottom: none;
  }
  .ant-table-wrapper .ant-table-tbody > tr.ant-table-placeholder:hover > td {
    background: transparent !important;
  }
  .ant-table-wrapper .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }
  /* Sorter icons sit right after the title; right-aligned (figure) columns
     keep their titles over the figures. */
  .ant-table-column-sorters {
    justify-content: flex-start;
    gap: 4px;
    .ant-table-column-title {
      flex: none;
    }
  }
  .ant-table-thead th.nl-end .ant-table-column-sorters {
    justify-content: flex-end;
  }
  .ant-table-pagination.ant-pagination {
    margin: 0;
    padding: 12px 16px 12px 20px;
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
